import { Box, FormField, FormHelp, Heading, Label, Select, Text } from '@zillow/constellation-10';

import { getFormattedPaymentMethod } from '@common/constants/PaymentMethod';
import { getVerifiedPaymentMethods } from '@common/paymentsUtils/multiplePaymentMethodsUtil';
import type {
  INewPaymentMethods,
  RenterMoveInCostsDetailsInterface,
  RenterPaymentMethodInterface,
  RenterPaymentSubscriptionInterface,
} from '@renter-hub/models/Types';

interface Props {
  paymentMethodToDelete: RenterPaymentMethodInterface;
  paymentMethods: Array<RenterPaymentMethodInterface>;
  paymentSubscriptionDetails: RenterPaymentSubscriptionInterface;
  moveInCostsDetails: RenterMoveInCostsDetailsInterface;
  selectedNewPaymentMethods: INewPaymentMethods;
  setSelectedNewPaymentMethods: (value: React.SetStateAction<INewPaymentMethods>) => void;
}

export const DeleteScheduledPaymentMethod = ({
  paymentMethodToDelete,
  paymentMethods,
  paymentSubscriptionDetails,
  moveInCostsDetails,
  selectedNewPaymentMethods,
  setSelectedNewPaymentMethods,
}: Props) => {
  const findCurrentPaymentMethod = (paymentMethodDetails) => {
    const currentPaymentMethod = paymentMethods.find(
      (paymentMethod) => paymentMethod.id === paymentMethodDetails.paymentMethodId,
    );
    return currentPaymentMethod || null;
  };

  const currentMoveInCostsPaymentMethod = moveInCostsDetails ? findCurrentPaymentMethod(moveInCostsDetails) : null;
  const currentMonthlyRentPaymentMethod = findCurrentPaymentMethod(paymentSubscriptionDetails);

  // Only show move-in costs if it's scheduled
  const shouldShowMoveInCosts = moveInCostsDetails?.isScheduled && currentMoveInCostsPaymentMethod;

  // Only show monthly rent if it has a scheduled date
  const shouldShowMonthlyRent =
    paymentSubscriptionDetails?.nextScheduledDate &&
    paymentSubscriptionDetails?.nextScheduledDate !== '0' &&
    currentMonthlyRentPaymentMethod;

  const activePaymentMethods = getVerifiedPaymentMethods(paymentMethods).filter(
    (paymentMethod) => paymentMethod.id !== paymentMethodToDelete.id,
  );

  const renderNewPaymentMethodOptionsModule = (): React.ReactNode => {
    const options = [
      <option key="default" value="" disabled hidden>
        Select
      </option>,
      ...(activePaymentMethods?.map((paymentMethod) => {
        return (
          <option key={paymentMethod.id} value={paymentMethod.id}>
            {`${paymentMethod.nickname || paymentMethod.displayName} ending in ${paymentMethod.accountLast4}`}
          </option>
        );
      }) ?? []),
    ];
    return options;
  };

  const onPaymentMethodChange = (e): void => {
    const key = e.target.dataset.statekey;
    const value = e.target.value;
    setSelectedNewPaymentMethods({
      ...selectedNewPaymentMethods,
      [key]: value,
    });
  };

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
      <Heading level={2} textStyle="heading-xs">
        Update your payment method
      </Heading>
      <Text>
        To delete{' '}
        <Text textStyle="body-bold">{`${getFormattedPaymentMethod(paymentMethodToDelete.accountType)}...${
          paymentMethodToDelete.accountLast4
        }`}</Text>
        , select a new payment method for the following payments:
      </Text>
      {shouldShowMoveInCosts && (
        <FormField
          label={<Label>Move-in payment</Label>}
          description={
            <FormHelp>
              {`Current method: ${getFormattedPaymentMethod(
                currentMoveInCostsPaymentMethod.accountType,
              )}...${currentMoveInCostsPaymentMethod?.accountLast4}`}
            </FormHelp>
          }
          control={
            <Select
              data-statekey="moveInCostsId"
              value={selectedNewPaymentMethods.moveInCostsId}
              onChange={onPaymentMethodChange}
            >
              {renderNewPaymentMethodOptionsModule()}
            </Select>
          }
        />
      )}
      {shouldShowMonthlyRent && (
        <FormField
          label={<Label>Monthly rent {`${paymentSubscriptionDetails?.recurring ? '(auto-pay)' : ''}`}</Label>}
          description={
            <FormHelp>
              {`Current method: ${getFormattedPaymentMethod(currentMonthlyRentPaymentMethod.accountType)}...${
                currentMonthlyRentPaymentMethod.accountLast4
              }`}
            </FormHelp>
          }
          control={
            <Select
              data-statekey="monthlyRentId"
              value={selectedNewPaymentMethods.monthlyRentId}
              onChange={onPaymentMethodChange}
            >
              {renderNewPaymentMethodOptionsModule()}
            </Select>
          }
        />
      )}
    </Box>
  );
};
